import React, { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, Star, MapPin } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getRentalProperties } from "../../../firebase/firestore";

const RentalsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [rentalProperties, setRentalProperties] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchRentalProperties = async () => {
      try {
        setIsLoading(true);
        const result = await getRentalProperties({ status: 'approved' });
        if (result.success) {
          setRentalProperties(result.data);
        }
      } catch (error) {
        console.error('Error fetching rental properties:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRentalProperties();
  }, []);

  const rentalVillas = rentalProperties.map((property, index) => ({
    id: property.id,
    name: property.propertyInfo?.name || "Rental Property",
    slug: property.propertyInfo?.slug || `rental-${index}`,
    location: property.propertyInfo?.address || "Location not specified",
    price: property.propertyInfo?.pricePerNight ? `$${property.propertyInfo.pricePerNight}` : "Price on request",
    rating: 4.5,
    image: property.media?.imageLinks?.[0] || "/placeholder-image.jpg",
    description: property.description?.split("\n")[0] || "Beautiful rental property",
    amenities: Array.isArray(property.amenities) ? property.amenities.slice(0, 8) : [],
    guests: property.accommodation?.maxGuests || "N/A",
    bedrooms: property.accommodation?.bedrooms || "N/A",
    bathrooms: property.accommodation?.bathrooms || "N/A",
  }));

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev === rentalVillas.length - 1 ? 0 : prev + 1));
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev === 0 ? rentalVillas.length - 1 : prev - 1));
  };

  const handleViewDetails = (propertySlug) => {
    navigate(`/rental/${propertySlug}`);
  };

  // Loading state
  if (isLoading) {
    return (
      <section id="rentals" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-serif font-bold text-gray-900 mb-4">
              Villa Rentals
            </h2>
            <p className="text-lg text-gray-600">Hand-picked selection of quality places</p>
          </div>
          <div className="flex justify-center py-20">
            <div className="text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-tropical-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading rental properties...</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // No properties state
  if (rentalVillas.length === 0) {
    return (
      <section id="rentals" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-serif font-bold text-gray-900 mb-4">
              Villa Rentals
            </h2>
            <p className="text-lg text-gray-600">Hand-picked selection of quality places</p>
          </div>
          <div className="text-center py-20">
            <div className="text-gray-400 text-6xl mb-4">No properties</div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">No rental properties available</h3>
            <p className="text-gray-600">Check back later for new listings.</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="rentals" className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-serif font-bold text-gray-900 mb-4">
            Villa Rentals
          </h2>
          <p className="text-lg text-gray-600">Hand-picked selection of quality places</p>
        </div>

        {/* Carousel Container */}
        <div className="relative">
          {/* Previous Arrow - Outside */}
          <button
            onClick={prevSlide}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 z-20 w-12 h-12 bg-white rounded-full shadow-xl flex items-center justify-center hover:scale-110 transition-all duration-300 lg:-left-6"
            aria-label="Previous villa"
          >
            <ChevronLeft className="text-gray-700" size={28} />
          </button>

          {/* Next Arrow - Outside */}
          <button
            onClick={nextSlide}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 z-20 w-12 h-12 bg-white rounded-full shadow-xl flex items-center justify-center hover:scale-110 transition-all duration-300 lg:-right-6"
            aria-label="Next villa"
          >
            <ChevronRight className="text-gray-700" size={28} />
          </button>

          {/* Slides */}
          <div className="overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div
              className="flex transition-transform duration-700 ease-in-out"
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
              {rentalVillas.map((villa) => (
                <div key={villa.id} className="w-full flex-shrink-0">
                  <div className="grid grid-cols-1 lg:grid-cols-2">
                    {/* Image */}
                    <div className="relative">
                      <img
                        src={villa.image}
                        alt={villa.name}
                        className="w-full h-64 sm:h-80 lg:h-full object-cover"
                      />
                      <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm rounded-full px-3 py-1.5 flex items-center space-x-1 shadow-md">
                        <Star className="text-yellow-500" size={16} fill="currentColor" />
                        <span className="text-sm font-bold text-gray-700">{villa.rating}</span>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-6 sm:p-8 lg:p-10 flex flex-col justify-between">
                      <div>
                        <h3 className="text-2xl sm:text-3xl font-serif font-bold text-gray-900 mb-2">
                          {villa.name}
                        </h3>
                        <div className="flex items-center text-gray-600 mb-4">
                          <MapPin size={18} className="mr-2" />
                          <span className="text-sm sm:text-base">{villa.location}</span>
                        </div>

                        <p className="text-gray-600 text-sm sm:text-base leading-relaxed mb-6 line-clamp-3">
                          {villa.description}
                        </p>

                        {/* Stats */}
                        <div className="grid grid-cols-3 gap-4 mb-6">
                          <div className="text-center">
                            <div className="text-xl font-bold text-tropical-600">{villa.guests}</div>
                            <div className="text-xs text-gray-500">Guests</div>
                          </div>
                          <div className="text-center">
                            <div className="text-xl font-bold text-tropical-600">{villa.bedrooms}</div>
                            <div className="text-xs text-gray-500">Bedrooms</div>
                          </div>
                          <div className="text-center">
                            <div className="text-xl font-bold text-tropical-600">{villa.bathrooms}</div>
                            <div className="text-xs text-gray-500">Bathrooms</div>
                          </div>
                        </div>

                        {/* Amenities */}
                        <div className="mb-6">
                          <div className="grid grid-cols-2 gap-3">
                            {villa.amenities.slice(0, 4).map((amenity, i) => (
                              <div key={i} className="flex items-center space-x-2">
                                <div className="w-2 h-2 bg-tropical-500 rounded-full flex-shrink-0"></div>
                                <span className="text-sm text-gray-600 truncate">{amenity}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Price + CTA */}
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-6 border-t border-gray-200">
                        <div>
                          <span className="text-3xl font-bold text-tropical-600">{villa.price}</span>
                          <span className="text-gray-600 ml-1">/ weekly</span>
                        </div>
                        <button
                          onClick={() => handleViewDetails(villa.slug)}
                          className="px-6 py-3 bg-tropical-600 hover:bg-tropical-700 text-white font-semibold rounded-lg transition-all duration-300 hover:scale-105 shadow-lg"
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Dots Indicator - Centered Below */}
          <div className="flex justify-center mt-8 space-x-2">
            {rentalVillas.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`transition-all duration-300 rounded-full ${
                  index === currentIndex
                    ? "w-10 h-3 bg-tropical-600"
                    : "w-3 h-3 bg-gray-300 hover:bg-gray-400"
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default RentalsSection;